import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { AceConfigInterface } from 'ngx-ace-wrapper';

declare let toastr: any;

@Component({
    selector: 'app-json-editor',
    templateUrl: './json-editor.component.html',
    styleUrls: ['./json-editor.component.scss'],
})
export class JsonEditorComponent implements OnInit, OnChanges {
    @Input() value: string = '';
    @Input() parsingStrategy: 'nested' | 'array-expanded' = 'nested';
    @Input() showCopyButton: boolean = true;

    @Output() valueChange = new EventEmitter<string>();

    viewType: 'json' | 'editor' = 'editor';
    editorData: { key: string; value: string }[] = [{ key: '', value: '' }];
    uniqueId: string = Math.random().toString(36).substring(2, 9);

    aceConfig: AceConfigInterface = {
        mode: 'json',
        theme: 'chrome',
        tabSize: 2,
        wrap: true,
        showPrintMargin: false,
        minLines: 5,
        maxLines: Infinity,
    };

    private initialized = false;

    ngOnInit() {
        this.parseJsonToEditor();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['value'] && this.initialized && this.viewType === 'editor') {
            // Only re-parse if value changed externally (not from our own emit)
            if (!changes['value'].firstChange) {
                const currentJson = this.editorToJson();
                const normalizedCurrent = this.normalizeJson(currentJson);
                const normalizedNew = this.normalizeJson(changes['value'].currentValue);
                if (normalizedCurrent !== normalizedNew) {
                    this.parseJsonToEditor();
                }
            }
        }
    }

    private normalizeJson(json: string): string {
        try {
            return JSON.stringify(JSON.parse(json || '{}'));
        } catch {
            return json || '';
        }
    }

    selectViewType(type: 'json' | 'editor') {
        if (type === 'editor' && this.viewType === 'json') {
            this.parseJsonToEditor();
        } else if (type === 'json' && this.viewType === 'editor') {
            this.parseEditorToJson();
        }
        this.viewType = type;
    }

    onJsonChange(newValue: string) {
        this.value = newValue;
        this.valueChange.emit(newValue);
    }

    parseJsonToEditor() {
        try {
            const obj = this.value ? (typeof this.value === 'string' ? JSON.parse(this.value) : this.value) : {};
            this.editorData = [];

            if (this.parsingStrategy === 'array-expanded') {
                this.parseArrayExpanded(obj);
            } else {
                this.parseNested(obj);
            }

            if (this.editorData.length === 0) {
                this.editorData.push({ key: '', value: '' });
            }
        } catch (e) {
            toastr.warning('JSON 파싱에 실패했습니다. JSON 형식을 확인해주세요.');
            this.editorData = [{ key: '', value: '' }];
        }
    }

    private parseNested(obj: any) {
        for (const [key, value] of Object.entries(obj)) {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                for (const [childKey, childValue] of Object.entries(value)) {
                    this.editorData.push({
                        key: `${key}.${childKey}`,
                        value: typeof childValue === 'object' ? JSON.stringify(childValue) : String(childValue),
                    });
                }
            } else {
                this.editorData.push({
                    key,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
                });
            }
        }
    }

    private parseArrayExpanded(obj: any) {
        const processValue = (key: string, value: any, depth: number) => {
            if (Array.isArray(value)) {
                if (depth === 0) {
                    value.forEach((item, idx) => {
                        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
                            for (const [itemKey, itemValue] of Object.entries(item)) {
                                processValue(`${key}[${idx}].${itemKey}`, itemValue, depth + 1);
                            }
                        } else {
                            this.editorData.push({
                                key: `${key}[${idx}]`,
                                value: typeof item === 'object' ? JSON.stringify(item) : String(item),
                            });
                        }
                    });
                } else {
                    this.editorData.push({
                        key,
                        value: JSON.stringify(value).replace(/,/g, ', '),
                    });
                }
            } else if (typeof value === 'object' && value !== null) {
                for (const [childKey, childValue] of Object.entries(value)) {
                    processValue(`${key}.${childKey}`, childValue, depth);
                }
            } else {
                this.editorData.push({
                    key,
                    value: value === null || value === undefined ? '' : String(value),
                });
            }
        };

        for (const [key, value] of Object.entries(obj)) {
            processValue(key, value, 0);
        }
    }

    parseEditorToJson() {
        const json = this.editorToJson();
        this.value = json;
        this.valueChange.emit(json);
    }

    private editorToJson(): string {
        if (this.parsingStrategy === 'array-expanded') {
            return this.editorToJsonArrayExpanded();
        } else {
            return this.editorToJsonNested();
        }
    }

    private editorToJsonNested(): string {
        const obj: any = {};
        for (const item of this.editorData) {
            if (item.key.trim()) {
                let parsedValue: any;
                try {
                    parsedValue = JSON.parse(item.value);
                } catch {
                    parsedValue = item.value;
                }

                if (item.key.includes('.')) {
                    const [parentKey, ...rest] = item.key.split('.');
                    const childKey = rest.join('.');
                    if (!obj[parentKey]) {
                        obj[parentKey] = {};
                    }
                    obj[parentKey][childKey] = parsedValue;
                } else {
                    obj[item.key] = parsedValue;
                }
            }
        }
        return JSON.stringify(obj, null, 2);
    }

    private editorToJsonArrayExpanded(): string {
        const obj: any = {};

        const setNestedValue = (target: any, path: string, value: any) => {
            const parts = path.split(/\.|\[|\]/).filter(p => p !== '');
            let current = target;

            for (let i = 0; i < parts.length - 1; i++) {
                const part = parts[i];
                const nextPart = parts[i + 1];
                const isNextArray = /^\d+$/.test(nextPart);

                if (!(part in current)) {
                    current[part] = isNextArray ? [] : {};
                }
                current = current[part];
            }

            const lastPart = parts[parts.length - 1];
            current[lastPart] = value;
        };

        for (const item of this.editorData) {
            if (item.key.trim()) {
                let parsedValue: any;
                try {
                    parsedValue = JSON.parse(item.value);
                } catch {
                    parsedValue = item.value;
                }
                setNestedValue(obj, item.key, parsedValue);
            }
        }

        return JSON.stringify(obj, null, 2);
    }

    addField() {
        this.editorData.push({ key: '', value: '' });
    }

    removeField(index: number) {
        this.editorData.splice(index, 1);
        if (this.editorData.length === 0) {
            this.editorData.push({ key: '', value: '' });
        }
        this.parseEditorToJson();
    }

    onFieldChange() {
        this.parseEditorToJson();
    }

    copyToClipboard() {
        if (!this.value) {
            toastr.warning('복사할 내용이 없습니다.');
            return;
        }
        navigator.clipboard.writeText(this.value).then(() => {
            toastr.success('클립보드에 복사되었습니다.');
        }).catch(() => {
            toastr.error('복사에 실패했습니다.');
        });
    }

    getArrayGroupIndex(key: string): number {
        let groupKey = '';

        const arrayMatch = key.match(/^([^\[]+\[\d+\])/);
        if (arrayMatch) {
            groupKey = arrayMatch[1];
        } else {
            const dotIndex = key.indexOf('.');
            if (dotIndex > 0) {
                groupKey = key.substring(0, dotIndex);
            }
        }

        if (!groupKey) {
            return -1;
        }

        let hash = 0;
        for (let i = 0; i < groupKey.length; i++) {
            hash = ((hash << 5) - hash) + groupKey.charCodeAt(i);
        }
        return Math.abs(hash) % 5;
    }
}
